import { BREAKPOINT } from '@constants/breakpoints'
import { useBreakpoint } from '@hooks/useBreakpoint'
import { useLazyService } from '@hooks/useLazyService'
import { markAsSpam } from '@services/leads/markAsSpam'
import { Button, DropdownElement, Icon, Spreader } from '@ui-kit'
import { Dispatch, Fragment, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

export const MarkAsSpam = ({
  identifiers,
  isSpam,
  refreshLeadsList,
  setSelectedLeads,
  onSuccess,
  variant = 'button'
}: {
  identifiers: string[]
  isSpam: boolean
  refreshLeadsList: () => void
  setSelectedLeads: Dispatch<SetStateAction<string[]>>
  onSuccess?: () => void
  variant?: 'button' | 'dropdown-element'
}) => {
  const { t } = useTranslation()

  const isBiggerThan1350 = useBreakpoint(BREAKPOINT[1350], 'min')

  const [markAsSpamRequest, { isLoading: isMarkAsSpamLoading }] =
    useLazyService(markAsSpam, {
      handleErrorMessages: true,
      t,
      successToastText: isSpam
        ? t('word.unmark-as-spam.success.toast')
        : t('word.mark-as-spam.success.toast'),
      onSuccess: () => {
        setSelectedLeads([])
        refreshLeadsList()
        onSuccess?.()
      }
    })

  const handleMarkAsSpam = () => markAsSpamRequest({ identifiers, revert: 0 })

  const handleUnmarkAsSpam = () => markAsSpamRequest({ identifiers, revert: 1 })

  const buttonLabel = isSpam ? t('word.unmark-as-spam') : t('word.mark-as-spam')

  if (variant === 'button') {
    return (
      <Button
        variant='text-neutral'
        icon='icon-block'
        onClick={isSpam ? handleUnmarkAsSpam : handleMarkAsSpam}
        disabled={isMarkAsSpamLoading}
        isLoading={isMarkAsSpamLoading}
      >
        {isBiggerThan1350 ? buttonLabel : ''}
      </Button>
    )
  }

  return (
    <DropdownElement
      disabled={isMarkAsSpamLoading}
      onClick={isSpam ? handleUnmarkAsSpam : handleMarkAsSpam}
    >
      {isMarkAsSpamLoading ? (
        <Icon icon='icon-spinner' spin data-testid='loaderIcon' />
      ) : (
        <Fragment>
          <Icon icon='icon-block' />
          <Spreader spread={10} />
          {buttonLabel}
        </Fragment>
      )}
    </DropdownElement>
  )
}
